// Call plugin.
import { callActions } from '../../interfaceNew/actions'
import { normalizeSipUri } from '../../utils/normalization'

// Other plugins.
import * as authActions from '../../../auth/interface/actions'
import * as authActionTypes from '../../../auth/interface/actionTypes'
import { logManager } from '../../../logs'

// Libraries.
import { put, actionChannel, take } from 'redux-saga/effects'
import BasicError, { callCodes } from '../../../errors'

const log = logManager.getLogger('CALL')
/**
 * Starts a new outgoing call.
 *
 * This saga defines how a call is started anonymously. It performs the
 *    signalling operations to create an "anonymous call" on the server.
 * Assumptions:
 *     1. None.
 * Responsibilities:
 *     1. Connect to callMe service.
 *     2. Dispatches action to make the call.
 * @method makeAnonymousCall
 * @param {Object}   action        An action of type `MAKE_ANONYMOUS_CALL`.
 */
export function * makeAnonymousCall (deps, action) {
  const anonUser = action.payload.callOptions.from
  if (!anonUser) {
    yield put(
      callActions.makeAnonymousCallFinish(action.payload.id, {
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: 'callOptions has no `from` property'
        })
      })
    )
    return
  }
  const { realm, accountToken, toToken, fromToken } = action.payload.credentials

  // If none of these 4 properties exist, then we're attempting a tokenless anonymous call.
  // If any of these 4 properties exist, then we're attempting a time-based token anonymous call.
  // So if some of them exist and some of them are missing, generate an error informing the user about it.
  if ((realm || accountToken || toToken || fromToken) && (!realm || !accountToken || !toToken || !fromToken)) {
    yield put(
      callActions.makeAnonymousCallFinish(action.payload.id, {
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message:
            'Time-based token anonymous call missing one or more of the following properties: `realm`, `accountToken`, `toToken`, `fromToken`.'
        })
      })
    )
    return
  }

  let account, caller, callee
  let isTokenBased
  if (realm && accountToken && toToken && fromToken) {
    // If credentials were provided for a token-limited call.
    log.debug('Performing time-limited token anonymous call.')
    account = accountToken
    caller = fromToken
    callee = toToken
    isTokenBased = true
  } else {
    log.debug('Performing anonymous call.')
    account = action.payload.callee
    caller = anonUser
    callee = action.payload.callee
    isTokenBased = false
  }

  const connectChannel = yield actionChannel(authActionTypes.CONNECT_FINISHED)
  yield put(
    authActions.connect({
      username: account,
      password: '',
      // The fake userId of the current, anonymous user.
      anonUsername: anonUser,
      realm
    })
  )
  const connectResponse = yield take(connectChannel)
  connectChannel.close()

  if (connectResponse.error) {
    // Connection failed.
    yield put(
      callActions.makeAnonymousCallFinish(action.payload.id, {
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: 'Failed to connect to server.'
        })
      })
    )
    return
  }

  // Make the call normally after we're subscribed.
  yield put(
    callActions.makeCall(action.payload.id, {
      participantAddress: isTokenBased ? callee : normalizeSipUri(callee),
      mediaConstraints: action.payload.mediaConstraints,
      isAnonymous: true,
      bandwidth: action.payload.callOptions.bandwidth,
      account,
      from: isTokenBased ? caller : normalizeSipUri(caller),
      displayName: isTokenBased ? '' : action.payload.callOptions.displayName,
      customParameters: action.payload.callOptions.customParameters
    })
  )
}
